import type { InterpretLensFiltersResponse } from '~/hooks/apis/reporting/useInterpretLensFilters';

import type { LensPeriod, LensPeriodComparison } from '~/types/leLens';

import { dateRangePresetById } from './lensPeriodPresets';

export type BoardFilterTarget = {
  key: string;
  label: string;
  options: ReadonlyArray<string>;
  multiple?: boolean;
  /** Hidden fields may still be recognised, but should not bloat the available-controls message. */
  visible?: boolean;
};

/** The two rows of the shelf: a date range with its anchor, or 'board' to follow the board's, and a comparison. */
export type BoardPeriodProposal = {
  dateRange?: Pick<LensPeriod, 'anchor' | 'window'> | 'board';
  compareTo?: LensPeriodComparison | null;
};

export type BoardFilterProposal = {
  kind: 'ready';
  changes: Array<string>;
  period?: BoardPeriodProposal;
  values?: Record<string, Array<string> | undefined>;
  clearValues?: boolean;
};

export type BoardFilterResult = BoardFilterProposal | { kind: 'error'; message: string };

/** Converts the server's allow-listed AI interpretation into the date range
 * and proposal shape the existing board controls already apply. */
export function boardFilterResultFromInterpretation(
  response: InterpretLensFiltersResponse,
  allowPeriod = true,
): BoardFilterResult {
  if (response.kind === 'error') {
    return response;
  }
  const { period, comparison, changes, ...rest } = response.proposal;
  const dateRange = !allowPeriod
    ? undefined
    : period?.kind === 'fixed'
      ? { anchor: 'yesterday' as const, window: { from: period.from, to: period.to } }
      : period?.kind === 'preset'
        ? period.preset === 'lensDefault'
          ? 'board'
          : presetPeriod(period.preset)
        : undefined;
  const keptChanges = allowPeriod
    ? changes
    : changes.filter((change) => !change.startsWith('Period:') && !change.startsWith('Comparison:'));
  const keptComparison = allowPeriod && comparison !== undefined ? legacyComparison(comparison) : undefined;
  if (changes.length && !keptChanges.length) {
    return {
      kind: 'error',
      message: 'This Lens has no date-aware cards. Ask for filter values instead of a reporting period.',
    };
  }
  return {
    kind: 'ready',
    ...rest,
    changes: keptChanges,
    ...proposedPeriod(dateRange, keptComparison),
  };
}

const LEGACY_COMPARISONS: Record<string, LensPeriodComparison> = {
  previousPeriod: 'previous',
  previousYear: { back: 1, unit: 'year' },
};

function legacyComparison(mode: 'previousYear' | 'previousPeriod' | null): LensPeriodComparison | null {
  return mode === null ? null : LEGACY_COMPARISONS[mode];
}

function presetPeriod(id: string): BoardPeriodProposal['dateRange'] {
  const preset = dateRangePresetById(id);
  return preset ? { anchor: preset.anchor ?? 'yesterday', window: preset.window } : undefined;
}

function proposedPeriod(
  dateRange: BoardPeriodProposal['dateRange'],
  compareTo: BoardPeriodProposal['compareTo'],
): { period?: BoardPeriodProposal } {
  if (dateRange === undefined && compareTo === undefined) {
    return {};
  }
  return {
    period: { ...(dateRange !== undefined ? { dateRange } : {}), ...(compareTo !== undefined ? { compareTo } : {}) },
  };
}

/** The viewer's override after a proposal: the date range replaces anchor and window together, the comparison its own key. */
export function viewerPeriodAfter(
  current: Partial<LensPeriod> | undefined,
  proposal: BoardPeriodProposal,
): Partial<LensPeriod> | undefined {
  const { anchor: _anchor, window: _window, ...withoutRange } = current ?? {};
  const next: Partial<LensPeriod> =
    proposal.dateRange === undefined
      ? { ...current }
      : proposal.dateRange === 'board'
        ? withoutRange
        : { ...withoutRange, ...proposal.dateRange };
  if (proposal.compareTo !== undefined) {
    next.comparison = proposal.compareTo;
  }
  return Object.keys(next).length ? next : undefined;
}

function normalise(value: string): string {
  return value.toLocaleLowerCase().replace(/[’']/g, '').replace(/\s+/g, ' ').trim();
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function containsPhrase(query: string, phrase: string): boolean {
  return new RegExp(`(?:^|[^a-z0-9])${escapeRegExp(normalise(phrase))}(?:$|[^a-z0-9])`, 'i').test(query);
}

const COMMON_FILTER_NAMES = [
  'supplier',
  'product',
  'market',
  'channel',
  'country',
  'region',
  'brand',
  'property',
  'offer',
  'customer',
] as const;

function availableControls(targets: ReadonlyArray<BoardFilterTarget>, allowPeriod = true): string {
  const fields = targets.filter((target) => target.visible !== false).map((target) => target.label);
  return [...(allowPeriod ? ['Date range', 'Compare to'] : []), ...fields].join(', ');
}

function joined(values: ReadonlyArray<string>): string {
  if (values.length < 2) {
    return values[0] ?? '';
  }
  return `${values.slice(0, -1).join(', ')} and ${values[values.length - 1]}`;
}

function requestedOnlyValue(rawQuery: string): string | undefined {
  const match = rawQuery.trim().match(/^[“"]?(.+?)[”"]?\s+only[.!]?$/i);
  return match?.[1]?.trim();
}

function requestedValueForTarget(rawQuery: string, target: BoardFilterTarget): string | undefined {
  const names = [target.label, target.key.replace(/^(?:declared|selection):/, '').replace(/_/g, ' ')];
  for (const name of names) {
    const match = rawQuery.match(
      new RegExp(`${escapeRegExp(name)}\\s+(?:is|equals|=)\\s+[“"]?(.+?)[”"]?(?:[,.]|$)`, 'i'),
    );
    if (match?.[1]?.trim()) {
      return match[1].trim();
    }
  }
  return undefined;
}

function unavailableNamedFilter(query: string, targets: ReadonlyArray<BoardFilterTarget>): string | undefined {
  const available = targets.filter(
    (target) => containsPhrase(query, target.label) || containsPhrase(query, target.key.replace(/_/g, ' ')),
  );
  if (available.length > 0) {
    return undefined;
  }

  return COMMON_FILTER_NAMES.find((name) => containsPhrase(query, name));
}

/**
 * Legacy deterministic parser retained for its focused grammar tests and as a
 * small pure utility. The drawer itself now uses the model-backed endpoint and
 * applies only the validated proposal returned by the service.
 */
export function parseBoardFilterRequest(
  rawQuery: string,
  targets: ReadonlyArray<BoardFilterTarget>,
  allowPeriod = true,
): BoardFilterResult {
  const query = normalise(rawQuery);
  if (!query) {
    return { kind: 'error', message: 'Describe the reporting period or filter values you want.' };
  }

  if (/\b(?:exclude|except|without)\b/.test(query) && !/\bwithout comparison\b/.test(query)) {
    return {
      kind: 'error',
      message: 'Exclusions are not supported here yet. Ask for the values you want to include.',
    };
  }

  const changes: Array<string> = [];
  let dateRange: BoardPeriodProposal['dateRange'];
  let comparison: BoardPeriodProposal['compareTo'];
  const periodMentioned =
    /\b(?:last|past)\s+\d+\s+days?\b|\byear\s+to\s+date\b|\bytd\b|\bthis\s+year\b|\b(?:lens|default|opening)\s+(?:period|dates?)\b|\b(?:period|dates?)\s+default\b|\bprevious\s+year\b|\bprevious\s+period\b/.test(
      query,
    );

  if (allowPeriod) {
    if (/\b(?:lens|default|opening)\s+(?:period|dates?)\b|\b(?:period|dates?)\s+default\b/.test(query)) {
      dateRange = 'board';
      changes.push('Period: Lens default');
    } else if (/\b(?:last|past)\s+30\s+days?\b/.test(query)) {
      dateRange = presetPeriod('last30CompletedDays');
      changes.push('Period: Last 30 days');
    } else if (/\b(?:last|past)\s+7\s+days?\b|\b(?:last|past)\s+week\b/.test(query)) {
      dateRange = presetPeriod('last7CompletedDays');
      changes.push('Period: Last 7 days');
    } else if (/\b(?:year\s+to\s+date|ytd|this\s+year)\b/.test(query)) {
      dateRange = presetPeriod('yearToDate');
      changes.push('Period: Year to date');
    }

    if (/\b(?:no|remove|clear|without)\s+comparison\b/.test(query)) {
      comparison = null;
      changes.push('Comparison: None');
    } else if (/\b(?:previous|prior|last)\s+year\b|\b(?:vs|versus)\s+(?:ly|last year)\b/.test(query)) {
      comparison = LEGACY_COMPARISONS.previousYear;
      changes.push('Comparison: Previous year');
    } else if (/\b(?:previous|prior)\s+period\b|\b(?:vs|versus)\s+(?:previous|prior)\s+period\b/.test(query)) {
      comparison = LEGACY_COMPARISONS.previousPeriod;
      changes.push('Comparison: Previous period');
    }
  }

  const clearValues = /\b(?:clear|reset|remove)\s+(?:all\s+)?filters?\b/.test(query);
  if (clearValues) {
    changes.push('Filters: All values');
  }

  const values: Record<string, Array<string> | undefined> = {};
  if (!clearValues) {
    const unavailable = unavailableNamedFilter(query, targets);
    if (unavailable) {
      return {
        kind: 'error',
        message: `${unavailable.charAt(0).toUpperCase()}${unavailable.slice(1)} isn't available on this Lens. Available controls: ${availableControls(targets, allowPeriod)}.`,
      };
    }

    const optionOwners = new Map<string, Set<string>>();
    targets.forEach((target) =>
      target.options.forEach((option) => {
        const key = normalise(option);
        optionOwners.set(key, new Set([...(optionOwners.get(key) ?? []), target.key]));
      }),
    );

    for (const target of targets) {
      const labelNamed = containsPhrase(query, target.label) || containsPhrase(query, target.key.replace(/_/g, ' '));
      const matched = target.options.filter((option) => {
        if (!containsPhrase(query, option)) {
          return false;
        }
        const owners = optionOwners.get(normalise(option));
        return labelNamed || owners?.size === 1;
      });

      if (labelNamed && matched.length === 0) {
        const requestedValue = requestedValueForTarget(rawQuery, target);
        const examples = target.options.slice(0, 5);
        return {
          kind: 'error',
          message: requestedValue
            ? `“${requestedValue}” isn't a ${target.label} value.${examples.length ? ` Available values include ${joined(examples)}.` : ''}`
            : `Choose a ${target.label} value shown below.`,
        };
      }
      if (!target.multiple && matched.length > 1) {
        return {
          kind: 'error',
          message: `${target.label} accepts one value at a time. Choose one and preview again.`,
        };
      }
      if (matched.length > 0) {
        values[target.key] = matched;
        changes.push(`${target.label}: ${matched.join(', ')}`);
      }
    }
  }

  if (changes.length === 0) {
    const onlyValue = requestedOnlyValue(rawQuery);
    const visibleFields = targets.filter((target) => target.visible !== false).map((target) => target.label);
    return {
      kind: 'error',
      message:
        !allowPeriod && periodMentioned
          ? `This Lens has no date-aware cards. Available controls: ${availableControls(targets, false)}.`
          : onlyValue
            ? `No match for “${onlyValue}” in ${joined(visibleFields)}. Add another filter below if it belongs to a different field.`
            : `I couldn't match that request. Available controls: ${availableControls(targets, allowPeriod)}.`,
    };
  }

  return {
    kind: 'ready',
    changes,
    ...proposedPeriod(dateRange, comparison),
    ...(Object.keys(values).length > 0 ? { values } : {}),
    ...(clearValues ? { clearValues: true } : {}),
  };
}
